import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { message, Space } from 'antd';

import { Button } from '../../../../components/antd/Button';
import { useChangePagination } from '../../../../hooks';
import { useGetCompaniesQuery } from '../../../../services/companyApi';
import { useLazyExportRegistersQuery } from '../../../../services/exportApi';
import { CompanyType } from '../../../../types';
import { PanelHeader } from '../panelHeader';
import { RegistersDrawer } from '../registersDrawer';
import { RegistersTable } from '../registersTable';
import { columns } from './columns';
import { CompanyForm } from './companyForm';

export function Company() {
  const PAGE_SIZE = 15;
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();
  const [searchQueryTerm, setSearchQueryTerm] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState<string[]>([]);

  const {
    data: companyData,
    isFetching: isFetchingCompanyData,
    refetch: refetchCompanyData,
  } = useGetCompaniesQuery({
    page: onChangePagination(),
    name_or_cnpj: searchQueryTerm,
    page_size: PAGE_SIZE,
    company_type:
      companyTypeFilter.length > 0 ? companyTypeFilter.join(',') : undefined,
  });

  // A resposta de getCompanies injeta uma empresa placeholder "Inexistente"
  // (id 0) usada como opção padrão nos selects. No cadastro ela não deve
  // aparecer, e a linha extra deixaria a página com 16 itens > page_size 15,
  // fazendo o antd paginar no client (quebrando a paginação server-side).
  const companyRows = (companyData?.results || []).filter(
    (company) => company.id !== 0
  );

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyType>(
    {} as CompanyType
  );
  const [exportRegisters, { isLoading: isExporting }] =
    useLazyExportRegistersQuery();

  async function handleExport() {
    try {
      await exportRegisters({
        exportType: 'companies',
        params: {
          search: searchQueryTerm,
          company_type:
            companyTypeFilter.length > 0
              ? companyTypeFilter.join(',')
              : undefined,
        },
      }).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  function actionButtons() {
    return (
      <Space size={18}>
        <Button
          icon={<DownloadOutlined />}
          size="large"
          onClick={handleExport}
          loading={isExporting}
        >
          Exportar XLS
        </Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={() => setIsFormVisible(true)}
        >
          Nova Empresa
        </Button>
      </Space>
    );
  }
  function onSelectCompany(company: Record<string, any>) {
    setIsFormVisible(true);
    setIsEdit(true);
    setSelectedCompany({
      id: company.id,
      name: company.name,
      trade_name: company.trade_name,
      phone_number: company.phone_number,
      email: company.email,
      cnpj: company.cnpj,
      company_type: company.company_type,
      address: company.address,
      nature_of_cargo: company.nature_of_cargo?.id || null,
      tin: company.tin,
      activity: company.activity,
      responsible_name: company.responsible_name,
      responsible_cpf: company.responsible_cpf,
      responsible_phone: company.responsible_phone,
      responsible_email: company.responsible_email,
      responsible_sector: company.responsible_sector,
      contacts: company.contacts,
      antaq_storage_code: company.antaq_storage_code,
    });
  }

  function onCloseForm() {
    setIsFormVisible(false);
    setIsEdit(false);
    setSelectedCompany({} as CompanyType);
  }

  function searchData(value: any) {
    setQueryPage(1);
    setSearchQueryTerm(value);
  }

  function handleTableChange(_pagination: any, filters: any) {
    const selectedTypes: string[] = filters.company_type || [];
    const changed =
      selectedTypes.length !== companyTypeFilter.length ||
      selectedTypes.some((t: string) => !companyTypeFilter.includes(t));
    if (changed) {
      setCompanyTypeFilter(selectedTypes);
      setQueryPage(1);
    }
  }

  return (
    <>
      <RegistersDrawer visible={isFormVisible} width={640}>
        <CompanyForm
          onClose={() => onCloseForm()}
          allCompaniesCount={companyData?.count}
          selectedCompany={selectedCompany}
          refetchCompanyData={refetchCompanyData}
          isEdit={isEdit}
          setQueryPage={setQueryPage}
        />
      </RegistersDrawer>
      <PanelHeader
        panelName="empresa"
        actionButtons={actionButtons()}
        onSearch={searchData}
      />
      <RegistersTable
        rowKey="id"
        columns={columns}
        onSelectItem={onSelectCompany}
        dataSource={companyRows}
        onChangePagination={onChangePagination}
        onTableChange={handleTableChange}
        total={companyData?.count}
        isLoading={isFetchingCompanyData}
        queryPage={queryPage}
        showSizeChanger={false}
        itemsPerPage={PAGE_SIZE}
      />
    </>
  );
}
